/**
 * Eliminación en cascada de una empresa y todos sus datos asociados.
 * Usa Firestore Batches (máx. 500 operaciones por batch) para atomicidad.
 */

import {
  collection,
  doc,
  getDocs,
  query,
  where,
  writeBatch,
  type DocumentReference,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';

const FIRESTORE_BATCH_SIZE = 500;

/**
 * Ejecuta eliminaciones en batches de hasta FIRESTORE_BATCH_SIZE.
 */
async function deleteRefsInBatches(refs: DocumentReference[]): Promise<void> {
  for (let i = 0; i < refs.length; i += FIRESTORE_BATCH_SIZE) {
    const chunk = refs.slice(i, i + FIRESTORE_BATCH_SIZE);
    const batch = writeBatch(db);
    chunk.forEach((ref) => batch.delete(ref));
    await batch.commit();
  }
}

/**
 * Obtiene referencias de todos los documentos de una colección con companyId dado.
 */
async function getRefsByCompany(
  collectionName: string,
  companyId: string
): Promise<DocumentReference[]> {
  const snap = await getDocs(
    query(
      collection(db, collectionName),
      where('companyId', '==', companyId)
    )
  );
  return snap.docs.map((d) => d.ref);
}

/**
 * Elimina la empresa y todos los datos asociados (usuarios, procesos, candidatos,
 * intentos, resultados, invitaciones, empleados, evaluaciones NOM-035 y clima, etc.).
 */
export async function deleteCompanyCascade(companyId: string): Promise<void> {
  const refsToDelete: DocumentReference[] = [];

  // 1. Procesos e intentos: necesitamos sus IDs para processBatteries, results y responses
  const processSnap = await getDocs(
    query(collection(db, 'processes'), where('companyId', '==', companyId))
  );
  const processIds = processSnap.docs.map((d) => d.id);
  processSnap.docs.forEach((d) => refsToDelete.push(d.ref));

  const attemptSnap = await getDocs(
    query(collection(db, 'attempts'), where('companyId', '==', companyId))
  );
  const attemptIds = attemptSnap.docs.map((d) => d.id);
  attemptSnap.docs.forEach((d) => refsToDelete.push(d.ref));

  // 2. Invites: necesitamos tokenHash para limpiar inviteTokenLookup
  const invitesSnap = await getDocs(
    query(collection(db, 'invites'), where('companyId', '==', companyId))
  );
  const tokenHashes: string[] = [];
  invitesSnap.docs.forEach((d) => {
    refsToDelete.push(d.ref);
    const tokenHash = d.data().tokenHash;
    if (typeof tokenHash === 'string') tokenHashes.push(tokenHash);
  });

  // 3. Colecciones por companyId
  const [
    reportLogsRefs,
    behavioralNotesRefs,
    climateAssessmentsRefs,
    nom35AssessmentsRefs,
    assessmentCampaignsRefs,
    cultureInvitesRefs,
    cultureAssessmentsRefs,
    cultureInviteTokenLookupRefs,
    employeesRefs,
    candidatesRefs,
    userInvitesRefs,
    usersRefs,
    complaintsRefs,
  ] = await Promise.all([
    getRefsByCompany('reportLogs', companyId),
    getRefsByCompany('behavioral_notes', companyId),
    getRefsByCompany('climate_assessments', companyId),
    getRefsByCompany('nom35_assessments', companyId),
    getRefsByCompany('assessment_campaigns', companyId),
    getRefsByCompany('culture_invites', companyId),
    getRefsByCompany('culture_assessments', companyId),
    getRefsByCompany('culture_invite_token_lookup', companyId),
    getRefsByCompany('employees', companyId),
    getRefsByCompany('candidates', companyId),
    getRefsByCompany('userInvites', companyId),
    getRefsByCompany('users', companyId),
    getRefsByCompany('complaints', companyId),
  ]);

  refsToDelete.push(
    ...reportLogsRefs,
    ...behavioralNotesRefs,
    ...climateAssessmentsRefs,
    ...nom35AssessmentsRefs,
    ...assessmentCampaignsRefs,
    ...cultureInvitesRefs,
    ...cultureAssessmentsRefs,
    ...cultureInviteTokenLookupRefs,
    ...employeesRefs,
    ...candidatesRefs,
    ...userInvitesRefs,
    ...usersRefs,
    ...complaintsRefs
  );

  // 4. results y responses: doc id = attemptId
  attemptIds.forEach((attemptId) => {
    refsToDelete.push(doc(db, 'results', attemptId));
    refsToDelete.push(doc(db, 'responses', attemptId));
  });

  // 5. processBatteries: doc id = processId
  processIds.forEach((processId) => {
    refsToDelete.push(doc(db, 'processBatteries', processId));
  });

  // 6. inviteTokenLookup: doc id = tokenHash
  tokenHashes.forEach((tokenHash) => {
    refsToDelete.push(doc(db, 'inviteTokenLookup', tokenHash));
  });

  // 7. Empresa
  refsToDelete.push(doc(db, 'companies', companyId));

  await deleteRefsInBatches(refsToDelete);
}

/**
 * Mismo comportamiento que {@link deleteCompanyCascade}: elimina la empresa y
 * datos relacionados (empleados, campañas, NOM-035, clima, procesos, baterías de proceso,
 * candidatos, invitaciones, intentos, resultados, quejas, notas conductuales, etc.)
 * mediante `writeBatch` en chunks de hasta 500 operaciones.
 */
export const deleteCompanyComplete = deleteCompanyCascade;
